import type { RealtimeChannel } from "@supabase/supabase-js";

import { supabase } from "@/integrations/supabase/client";
import { presentMembers, type RoomPeek } from "@/engine/state/roomPeek";
import { isRoomCode } from "@/engine/state/roomIdentity";

/**
 * A look at a room from outside it.
 *
 * "Is anybody in there" was answerable only by going in: everything about who
 * is in a room lives on its presence channel, and a visitor joined that channel
 * by entering. So the one question somebody holding a code actually has was
 * answered after they had committed to the answer.
 *
 * This subscribes to the same channel and **never calls `track`**. That is the
 * whole of the read-only guarantee, and it is one line rather than a mechanism:
 * presence lists the members who tracked themselves, so a subscriber who does
 * not track is in nobody's list, takes no seat, and publishes no name.
 *
 * It reads `positions:<code>`, which is where a participant publishes itself,
 * rather than opening a channel of its own — a second channel would be a second
 * thing to keep in step with what a member is.
 */

/** How long a channel is given to answer before the room is called silent. */
export const PEEK_TIMEOUT_MS = 4000;

export interface PeekDeps {
  /** The channel to look through, or null where this tab is already on it. */
  channel?: (name: string) => RealtimeChannel | null;
  /** Gives it back, so the next taker of the topic is given a fresh one. */
  close?: (channel: RealtimeChannel) => void;
  /** Test seam: the timer, so a timeout is provable without waiting for one. */
  setTimer?: (fn: () => void, ms: number) => number;
  clearTimer?: (handle: number) => void;
}

/** A channel somebody is on, or on their way off. Not the peek's to take. */
const inUse = (channel: RealtimeChannel) =>
  channel.state === "joined" ||
  channel.state === "joining" ||
  channel.state === "leaving";

/**
 * The channel for a topic, or null where this tab is on it already.
 *
 * The client keeps one channel per topic and hands that same one to everybody
 * who asks for it. So a peek at a room this tab is already standing in is not
 * given a channel of its own: it is given the room's, where adding a presence
 * listener after `subscribe()` throws, and where the peek's own stop would
 * close the channel the room is talking on. Either way the room dies of being
 * looked at, which is the one thing a look must never do.
 *
 * Refusing is not a failure to report. A peek that was never taken says
 * nothing, and nothing is what is true: whoever holds that topic knows what is
 * in the room far better than a look from outside would.
 *
 * A channel nobody is on any more is a different matter. It is the leftover of
 * a room somebody has left, and taking it over is how that room can be looked
 * at again — which is the whole point of a list you can go back through.
 */
const takeChannel = (name: string): RealtimeChannel | null => {
  // The prefix is the client's, and is what `getChannels()` reports a topic as.
  const held = supabase
    .getChannels()
    .find((channel) => channel.topic === `realtime:${name}`);
  return held && inUse(held) ? null : supabase.channel(name);
};

/**
 * Watches a room from outside, reporting what it finds until it is stopped.
 *
 * Returns the function that stops it. Calling that gives the channel back,
 * which is the only thing keeping the peek from costing anything: a visitor who
 * types a code, thinks better of it and types another must not leave the first
 * one subscribed behind them — and the topic has to be free for whoever wants
 * it next, which is usually the room itself.
 *
 * A room this tab is already on is not looked at, and the stop for one of those
 * is a stop over nothing. See `takeChannel`.
 */
export function peekRoom(
  code: string,
  onReport: (peek: RoomPeek) => void,
  deps: PeekDeps = {},
): () => void {
  const open = deps.channel ?? takeChannel;
  const close =
    deps.close ??
    ((channel: RealtimeChannel) => void supabase.removeChannel(channel));
  const setTimer =
    deps.setTimer ??
    ((fn: () => void, ms: number) => window.setTimeout(fn, ms));
  const clearTimer =
    deps.clearTimer ?? ((handle: number) => window.clearTimeout(handle));

  let stopped = false;
  const report = (peek: RoomPeek) => {
    if (!stopped) onReport(peek);
  };

  // What is not a room code is not looked behind: no channel is joined for
  // one (#693).
  if (!isRoomCode(code)) return () => {};
  const channel = open(`positions:${code}`);
  if (!channel) return () => {};

  report({ kind: "asking" });
  // A channel that never subscribes reports nothing at all, so silence has to
  // be decided by a clock rather than waited for.
  let timer: number | null = setTimer(() => {
    timer = null;
    report({ kind: "silent" });
  }, PEEK_TIMEOUT_MS);

  const settle = () => {
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
  };

  channel.on("presence", { event: "sync" }, () => {
    settle();
    // Who is there is read off what the members already published, not asked
    // for: the look still tracks nothing, so it is still in nobody's list.
    const present = presentMembers(channel.presenceState());
    report({ kind: "answered", count: present.length, present });
  });

  channel.subscribe((status) => {
    if (status === "SUBSCRIBED") return;
    // `CHANNEL_ERROR`, `TIMED_OUT` and `CLOSED` all mean the same thing to
    // somebody looking at a door: nothing answered.
    settle();
    report({ kind: "silent" });
  });

  return () => {
    stopped = true;
    settle();
    close(channel);
  };
}
